import React from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import styles from "./dashboard.module.css";
import Logo from "../Logo/Logo";
import { Link } from "react-router";

import NavBar from "../NavBar/NavBar";
import { CalibrationBanners } from "../CalibrationBanners/CalibrationBanners";
import { NavLink, Outlet, useParams, useNavigate } from "react-router";
import Breadcrumbs from "../Breadcrumbs/Breadcrumb";
import classNames from "classnames";

import KeyboardDoubleArrowLeft from "@mui/icons-material/KeyboardDoubleArrowLeft";
import MenuIcon from "@mui/icons-material/Menu";

const drawerWidth = 190;

type MenuLinkProps = {
  to: string;
  navigate: (to: string) => void;
  children: React.ReactNode;
};

function MenuLink({ to, navigate, children }: MenuLinkProps): React.ReactNode {
  return (
    <li className={styles.dashboardListItem}>
      <NavLink
        to={to}
        className={({ isActive }) =>
          classNames({
            [styles.dashboardListItemAnchor]: true,
            [styles.active]: isActive,
          })
        }
        onClick={() => navigate(to)}
      >
        {children}
      </NavLink>
    </li>
  );
}

interface ResponsiveDrawerProps {
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
  desktopOpen: boolean;
  setDesktopOpen: (open: boolean) => void;
  children: React.ReactNode;
}

export function ResponsiveDrawer({
  mobileOpen,
  handleDrawerToggle,
  desktopOpen,
  setDesktopOpen,
  children,
}: ResponsiveDrawerProps) {
  const { customerId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const closeDrawerAndNavigate = (path: string) => {
    handleDrawerToggle();
    navigate(path);
  };

  const drawer = (
    <div>
      {customerId ? <ShowLogo /> : ""}
      <ul className={styles.dashboardList}>
        <MenuLink
          to={`/customer/${customerId}/field`}
          navigate={closeDrawerAndNavigate}
        >
          {t("menu.fields")}
        </MenuLink>
        <MenuLink
          to={`/customer/${customerId}/support`}
          navigate={closeDrawerAndNavigate}
        >
          {t("menu.Support")}
        </MenuLink>
        <MenuLink
          to={`/customer/${customerId}/treescout`}
          navigate={closeDrawerAndNavigate}
        >
          {t("menu.Treescout")}
        </MenuLink>
      </ul>
    </div>
  );

  return (
    <div className={styles.dashboard}>
      <Drawer
        container={document.body}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: "var(--color-green-dark)",
            color: "var(--color-secondary-mint)",
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="persistent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: "var(--color-green-dark)",
          },
          zIndex: 0,
        }}
        open={desktopOpen}
      >
        {drawer}
      </Drawer>
      {desktopOpen ? (
        <button
          onClick={() => setDesktopOpen(false)}
          className={`${styles.drawerClose} ${styles.drawerButton}`}
        >
          <KeyboardDoubleArrowLeft />
        </button>
      ) : (
        <button
          onClick={() => setDesktopOpen(true)}
          className={`${styles.drawerOpen} ${styles.drawerButton}`}
        >
          <MenuIcon />
        </button>
      )}
      <Box
        className={styles.dashboardContent}
        sx={{
          flexGrow: 1,
          marginLeft: { sm: desktopOpen ? `${drawerWidth}px` : `0` },
        }}
      >
        <Breadcrumbs mobileView={true} />
        <CalibrationBanners />
        {children}
      </Box>
    </div>
  );
}

function ShowLogo() {
  return (
    <div className={styles.logoContainer}>
      <Link to="">
        <Logo width={90} height={38} />
      </Link>
    </div>
  );
}

export default function Dashboard({
  children = <Outlet />,
}: {
  children?: React.ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [desktopOpen, setDesktopOpen] = React.useState(true);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <>
      <NavBar
        handleDrawerToggle={handleDrawerToggle}
        drawerWidth={desktopOpen ? drawerWidth : 20}
        hasDrawer={true}
      />
      <ResponsiveDrawer
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        desktopOpen={desktopOpen}
        setDesktopOpen={setDesktopOpen}
      >
        {children}
      </ResponsiveDrawer>
    </>
  );
}
